import api from './api';
import type {
  EpiKpisContract,
  EpiMatrizContract,
  EpiMapaCargoItemContract,
  EpiEstoqueSaldoContract,
  EpiMovimentacaoEstoqueContract,
  EpiCronogramaVigenciaItemContract,
  EpiRadarDemissionalItemContract,
  EpiDevolucaoItemContract,
  EpiEntregaContract,
  RegistrarEntradaCompraPayload,
  RegistrarBaixaAvariaPayload,
  CriarEntregaEpiPayload,
  AssinarEntregaEpiPayload,
  RegistrarDevolucaoPayload,
} from '@sstexames/shared-types';

export const episService = {
  async getKpis(empresaId?: number): Promise<EpiKpisContract> {
    const params = empresaId ? { empresaId } : {};
    const response = await api.get<EpiKpisContract>('/epis/kpis', { params });
    return response.data;
  },

  async getMatriz(empresaId?: number): Promise<EpiMatrizContract> {
    const params = empresaId ? { empresaId } : {};
    const response = await api.get<EpiMatrizContract>('/epis/matriz', { params });
    return response.data;
  },

  async getMapaCargos(empresaId?: number): Promise<EpiMapaCargoItemContract[]> {
    const params = empresaId ? { empresaId } : {};
    const response = await api.get<EpiMapaCargoItemContract[]>('/epis/mapa-cargos', { params });
    return response.data;
  },

  async getEstoqueSaldos(empresaId?: number): Promise<EpiEstoqueSaldoContract[]> {
    const params = empresaId ? { empresaId } : {};
    const response = await api.get<EpiEstoqueSaldoContract[]>('/epis/estoque', { params });
    return response.data;
  },

  async getMovimentacoesEstoque(empresaId?: number): Promise<EpiMovimentacaoEstoqueContract[]> {
    const params = empresaId ? { empresaId } : {};
    const response = await api.get<EpiMovimentacaoEstoqueContract[]>('/epis/movimentacoes', { params });
    return response.data;
  },

  async registrarEntradaCompra(payload: RegistrarEntradaCompraPayload): Promise<EpiEstoqueSaldoContract> {
    const response = await api.post<EpiEstoqueSaldoContract>('/epis/compras', payload);
    return response.data;
  },

  async registrarBaixaAvaria(payload: RegistrarBaixaAvariaPayload): Promise<EpiEstoqueSaldoContract> {
    const response = await api.post<EpiEstoqueSaldoContract>('/epis/baixas', payload);
    return response.data;
  },

  async getCronogramaVigencias(empresaId?: number): Promise<EpiCronogramaVigenciaItemContract[]> {
    const params = empresaId ? { empresaId } : {};
    const response = await api.get<EpiCronogramaVigenciaItemContract[]>('/epis/vigencias', { params });
    return response.data;
  },

  async getRadarDemissional(empresaId?: number): Promise<EpiRadarDemissionalItemContract[]> {
    const params = empresaId ? { empresaId } : {};
    const response = await api.get<EpiRadarDemissionalItemContract[]>('/epis/radar-demissional', { params });
    return response.data;
  },

  async getDevolucoes(empresaId?: number): Promise<EpiDevolucaoItemContract[]> {
    const params = empresaId ? { empresaId } : {};
    const response = await api.get<EpiDevolucaoItemContract[]>('/epis/devolucoes', { params });
    return response.data;
  },

  async registrarDevolucao(payload: RegistrarDevolucaoPayload): Promise<{ processados: number }> {
    const response = await api.post<{ processados: number }>('/epis/devolucoes', payload);
    return response.data;
  },

  async criarEntrega(payload: CriarEntregaEpiPayload): Promise<EpiEntregaContract> {
    const response = await api.post<EpiEntregaContract>('/epis/entregas', payload);
    return response.data;
  },

  async getEntrega(id: number, empresaId?: number): Promise<EpiEntregaContract> {
    const params = empresaId ? { empresaId } : {};
    const response = await api.get<EpiEntregaContract>(`/epis/entregas/${id}`, { params });
    return response.data;
  },

  async assinarEntrega(id: number, payload: AssinarEntregaEpiPayload): Promise<EpiEntregaContract> {
    const response = await api.post<EpiEntregaContract>(`/epis/entregas/${id}/assinar`, payload);
    return response.data;
  },

  async getEntregas(
    empresaId?: number,
    params?: { status?: string; search?: string; colaboradorId?: number },
  ): Promise<EpiEntregaContract[]> {
    const response = await api.get<EpiEntregaContract[]>('/epis/entregas', {
      params: { ...(empresaId ? { empresaId } : {}), ...params },
    });
    return response.data;
  },

  async downloadPdf(id: number, empresaId?: number): Promise<Blob> {
    const params = empresaId ? { empresaId } : {};
    const response = await api.get(`/epis/entregas/${id}/pdf`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  async downloadPdfFile(id: number, filename?: string, empresaId?: number): Promise<void> {
    const blob = await this.downloadPdf(id, empresaId);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `ficha_epi_${id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  // --------------------------------------------------------------------------
  // Métodos Públicos (Para Assinatura pelo Colaborador via Link Móvel/QR Code)
  // --------------------------------------------------------------------------
  async getEntregaPublica(token: string): Promise<EpiEntregaContract> {
    const response = await api.get<EpiEntregaContract>(`/public/epis/${token}`);
    return response.data;
  },

  async assinarEntregaPublica(token: string, payload: AssinarEntregaEpiPayload): Promise<EpiEntregaContract> {
    const response = await api.post<EpiEntregaContract>(`/public/epis/${token}/assinar`, payload);
    return response.data;
  },

  async downloadPdfPublico(token: string): Promise<Blob> {
    const response = await api.get(`/public/epis/${token}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },
};
